import React from 'react'
// libs
import { client } from 'lib/apollo-client'
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
// services
import { OfflineSyncService } from 'services/offlineSync'
import { OfflineStorageService } from 'services/offlineStorage'
// store
import { setSyncing } from 'store/reducers/offlineRisk'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'

const OfflineSyncContainer = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const user = useCurrentUser()

  const storage = React.useMemo(() => OfflineStorageService.getInstance(), [])
  const syncService = React.useMemo(() => new OfflineSyncService(client), [])

  const handleSync = React.useCallback(async () => {
    const mutations = storage.getMutationsInOrder()

    if (mutations.length === 0) {
      return
    }

    // OfflineSyncContainer is rendered outside <AppRoutes>, so useParams() always returns {}.
    // Extract the risk id directly from pathname using matchPath.
    const match = matchPath('/forms/edit/:id/template/:templateId', router.pathname)
    const currentRiskIdInUrl = match?.params?.id
    const pendingCreate = mutations.find((m) => m.type === 'riskCreate' && m.tempId === currentRiskIdInUrl)
    const pendingTemplateId = pendingCreate?.variables?.input?.riskTemplateId

    dispatch(setSyncing(true))

    const currentRiskId = await syncService.syncPendingMutations(currentRiskIdInUrl ?? undefined)

    if (currentRiskIdInUrl && currentRiskId && pendingTemplateId) {
      router.replace(`/forms/edit/${currentRiskId}/template/${pendingTemplateId}`)
    }

    dispatch(setSyncing(false))
  }, [router.pathname])

  // Sync pending mutations once the user ID is available in Redux.
  // Using user.id as dep (not []) because after re-login currentUser
  // may not be set yet when the component first mounts — loadState()
  // needs the user.id to find the correct localStorage queue.
  React.useEffect(() => {
    if (user?.id) {
      handleSync()
    }
  }, [user?.id])

  // Trigger sync when session is recovered after an offline period.
  // This fires after SessionManager confirms the token is valid — avoiding
  // the race condition where sync starts before the token is refreshed.
  React.useEffect(() => {
    window.addEventListener('skefto-network-recovered', handleSync)

    return () => {
      window.removeEventListener('skefto-network-recovered', handleSync)
    }
  }, [handleSync])

  return <></>
}

export default OfflineSyncContainer
